/**
 * EcosystemPortal — KISSED Ecosystem gateway (refined per the Phase-1 polish).
 *
 * Concierge-luxury feel. NOT corporate. NOT a dashboard. NOT a directory.
 * Palette: White (#FAF7FB), Black (#0A0A14), Royal Purple (#5C1E8A / #3D2266),
 * Silver (#C0C5CE tints), Hint of Green (#2D6B50).
 *
 * Mounted at `/portal` until DNS + sign-off make it the root of kissedbycvon.com.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, ShieldCheck, Compass } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";


const DOORS = [
  {
    key: "clinic",
    eyebrow: "Clinic Door™",
    headline: "Restore What Matters Most",
    subtext: "Clinical wellness, trichology, hair restoration, herbal wellness, and personalized support designed to uncover root causes and support healing.",
    cta: "Enter Clinic",
    href: "/clinic",
    external: false,
    badge: null,
    surface: "from-[#150B27] via-[#1B0F2E] to-[#3D2266]",
    glow: "from-[#5C1E8A] to-[#3D2266]",
    glyph: "✦",
  },
  {
    key: "coaching",
    eyebrow: "Coaching Door™",
    headline: "Become Who You Were Created To Be",
    subtext: "Identity work, emotional healing, personal transformation, coaching programs, consulting, and business intensives.",
    cta: "Enter Coaching",
    href: "https://christyvonta.com",
    external: true,
    badge: null,
    surface: "from-[#1B0F2E] via-[#3D2266] to-[#5C1E8A]",
    glow: "from-[#3D2266] to-[#150B27]",
    glyph: "❋",
  },
  {
    key: "university",
    eyebrow: "University Door™",
    headline: "Learn. Grow. Lead.",
    subtext: "Professional certification programs, continuing education, clinical training, degree pathways, and lifelong learning.",
    cta: "Enter University",
    href: "https://kisseduniversity.com",
    external: true,
    badge: "Admissions Now Open",
    surface: "from-[#0F0820] via-[#3D2266] to-[#5C1E8A]",
    glow: "from-[#5C1E8A] to-[#0F0820]",
    glyph: "✧",
  },
  {
    key: "comfort-care",
    eyebrow: "Comfort Care Door™",
    headline: "Compassion When It Matters Most",
    subtext: "Personal care, hospice support, senior services, family support, and dignity-centered care.",
    cta: "Enter Comfort Care",
    href: "/comfort-care",
    external: false,
    badge: null,
    surface: "from-[#102218] via-[#1B0F2E] to-[#2D6B50]",
    glow: "from-[#2D6B50] to-[#102218]",
    glyph: "✿",
  },
];


export default function EcosystemPortal() {
  return (
    <div className="min-h-screen bg-[#FAF7FB]" data-testid="ecosystem-portal">
      {/* Hero — concierge dark, luminous */}
      <section className="relative bg-[#0A0A14] text-white overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/4 w-[40rem] h-[40rem] rounded-full bg-purple-700/25 blur-3xl" />
          <div className="absolute top-20 right-0 w-[30rem] h-[30rem] rounded-full bg-fuchsia-700/15 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-emerald-700/15 blur-3xl" />
          {/* Faint silver grain via radial */}
          <div className="absolute inset-0 opacity-[0.04]"
               style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
        </div>

        <header className="relative max-w-6xl mx-auto px-6 py-7 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="portal-logo">
            <img src={logo} alt="KISSED" className="h-10 w-auto" />
          </Link>
          <a
            href="mailto:c.von@kissedbycvon.com"
            className="text-[11px] uppercase tracking-[0.28em] text-purple-100 hover:text-white transition"
            data-testid="portal-contact"
          >
            Contact
          </a>
        </header>

        <div className="relative max-w-4xl mx-auto px-6 pt-14 pb-24 text-center">
          <span
            className="kcis-anim-fade-up inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[10px] uppercase tracking-[0.32em] text-purple-100 font-semibold backdrop-blur-sm"
            style={{ animationDelay: "120ms" }}
            data-testid="portal-pill"
          >
            <Sparkles className="w-3 h-3" /> The KISSED Ecosystem
          </span>

          <h1
            data-testid="portal-headline"
            className="kcis-anim-fade-up mt-8 text-[2.6rem] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, animationDelay: "380ms" }}
          >
            Welcome to the<br className="hidden sm:inline" /> KISSED Ecosystem
          </h1>

          <div
            data-testid="portal-mantra"
            className="kcis-anim-fade-up mt-9 text-lg sm:text-xl text-purple-100 leading-relaxed"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, animationDelay: "720ms" }}
          >
            Heal the Root. &nbsp;·&nbsp; Empower the Vision. &nbsp;·&nbsp; Support the Structure.
          </div>

          <p
            className="kcis-anim-fade-up mt-7 text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed"
            style={{ animationDelay: "1040ms" }}
          >
            Four doors, each with its own purpose. Choose the journey that best aligns with what you need today — whether that is clinical care, personal transformation, professional growth, or compassionate support for a loved one.
          </p>

          <div
            className="kcis-anim-fade-in mt-12 inline-flex items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-purple-200/90"
            style={{ animationDelay: "1340ms" }}
          >
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Private &amp; Secure</span>
            <span className="w-px h-3 bg-white/20" />
            <span className="inline-flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> Concierge-Designed</span>
          </div>
        </div>
      </section>

      {/* Four Doors */}
      <main className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12 kcis-anim-fade-up" style={{ animationDelay: "1500ms" }}>
          <span className="text-[10px] uppercase tracking-[0.32em] text-purple-900 font-semibold">Choose Your Door</span>
          <h2
            className="mt-3 text-3xl sm:text-4xl text-slate-900"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
          >
            Where does your story begin?
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-7" data-testid="portal-doors">
          {DOORS.map((d, i) => (
            <DoorCard key={d.key} d={d} index={i} />
          ))}
        </div>

        {/* Subtle KCIS attribution between doors and footer */}
        <p className="mt-16 text-center text-[11px] uppercase tracking-[0.28em] text-slate-500" data-testid="portal-kcis-attribution">
          One operating system. Four doors. Endless care.
        </p>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A14] text-purple-100" data-testid="portal-footer">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <FooterCol
              title="KISSED Trichology &amp; Wellness Clinic"
              lines={["Wellness Assessment", "Clinical Deep Dive™", "Microscopic Hair & Scalp Analysis", "Trichology · Herbal Wellness"]}
              cta={{ to: "/clinic", label: "Enter Clinic" }}
            />
            <FooterCol
              title="Christy Vonta Coaching &amp; Consulting"
              lines={["Identity & Wellness Alignment", "The Me I See™ · She Is™", "The Aligned Empire™", "Coaching · Speaking"]}
              cta={{ to: "https://christyvonta.com", label: "Enter Coaching", external: true }}
            />
            <FooterCol
              title="KISSED University"
              lines={["Admissions Now Open", "Programs · Certifications", "Clinical Residency", "Continuing Education"]}
              cta={{ to: "https://kisseduniversity.com", label: "Enter University", external: true }}
            />
            <FooterCol
              title="KISSED Comfort Care"
              lines={["Senior Living Services", "Hospice & End-of-Life", "Bedside Grooming", "Family Support"]}
              cta={{ to: "/comfort-care", label: "Enter Comfort Care" }}
            />
          </div>

          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p
              className="text-sm text-slate-300 italic"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
            >
              &ldquo;We kiss the mind, teach the wisdom, and make the mission better.&rdquo;
            </p>
            <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500" data-testid="portal-footer-kcis">
              Powered by KCIS
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}


function DoorCard({ d, index = 0 }) {
  const inside = (
    <>
      {/* Luminous corner glow */}
      <div
        aria-hidden="true"
        className={`absolute -top-32 -right-24 w-96 h-96 rounded-full bg-gradient-to-br ${d.glow} opacity-30 blur-3xl group-hover:opacity-60 transition-opacity duration-700`}
      />
      {/* Top row — eyebrow + badge + glyph */}
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-purple-200 font-semibold">KCIS</div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-purple-100/80 font-semibold mt-1">{d.eyebrow}</div>
        </div>
        <div className="flex items-center gap-3">
          {d.badge ? (
            <span
              className="text-[10px] uppercase tracking-[0.22em] font-semibold bg-[#2D6B50]/95 text-white px-3 py-1 rounded-full"
              data-testid={`portal-door-${d.key}-badge`}
            >
              {d.badge}
            </span>
          ) : null}
          <span aria-hidden="true" className="text-purple-200/70 text-2xl select-none">{d.glyph}</span>
        </div>
      </div>

      {/* Headline + subtext */}
      <div className="relative mt-8">
        <h3
          data-testid={`portal-door-${d.key}-headline`}
          className="text-3xl sm:text-4xl leading-[1.08] tracking-tight text-white"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
        >
          {d.headline}
        </h3>
        <p className="mt-4 text-sm sm:text-[15px] text-slate-200/90 leading-relaxed max-w-[36ch]">
          {d.subtext}
        </p>
      </div>

      {/* Hairline separator */}
      <div className="relative mt-9 mb-5 h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent" />

      {/* CTA */}
      <div className="relative flex items-center justify-between">
        <span
          data-testid={`portal-door-${d.key}-cta`}
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-purple-100 font-semibold group-hover:text-white transition"
        >
          {d.cta}
          <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-purple-300/70">
          {d.external ? "External" : "Continue →"}
        </span>
      </div>
    </>
  );

  // Doors stagger AFTER the section header (1500ms) reveals.
  // 1700 → Clinic, 1900 → Coaching, 2100 → University, 2300 → Comfort Care.
  const staggerDelay = 1700 + index * 200;
  const sharedClass = `kcis-anim-fade-up relative group overflow-hidden rounded-3xl bg-gradient-to-br ${d.surface} text-white p-8 sm:p-10 shadow-xl shadow-purple-950/20 hover:shadow-2xl hover:shadow-purple-900/40 transition min-h-[360px]`;
  const sharedStyle = { animationDelay: `${staggerDelay}ms` };

  if (d.external) {
    return (
      <a
        href={d.href}
        rel="noopener noreferrer"
        data-testid={`portal-door-${d.key}`}
        className={sharedClass}
        style={sharedStyle}
      >
        {inside}
      </a>
    );
  }
  return (
    <Link
      to={d.href}
      data-testid={`portal-door-${d.key}`}
      className={sharedClass}
      style={sharedStyle}
    >
      {inside}
    </Link>
  );
}


function FooterCol({ title, lines, cta }) {
  return (
    <div>
      <div
        className="text-[13px] sm:text-sm text-white leading-snug"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <ul className="mt-3 space-y-1 text-[12px] text-slate-300/90 leading-relaxed">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      {cta ? (
        cta.external ? (
          <a
            href={cta.to}
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-4 text-[10px] uppercase tracking-[0.22em] text-purple-200 hover:text-white font-semibold"
          >
            {cta.label}
            <ArrowUpRight className="w-3 h-3" />
          </a>
        ) : (
          <Link
            to={cta.to}
            className="inline-flex items-center gap-1.5 mt-4 text-[10px] uppercase tracking-[0.22em] text-purple-200 hover:text-white font-semibold"
          >
            {cta.label}
            <ArrowUpRight className="w-3 h-3" />
          </Link>
        )
      ) : null}
    </div>
  );
}
